import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";

const LANGUAGES = [
  {
    value: "en",
    label: "English",
    flagClass: "language-select__flag-image--en",
  },
  {
    value: "de",
    label: "Deutsch",
    flagClass: "language-select__flag-image--de",
  },
  {
    value: "nl",
    label: "Dutch",
    flagClass: "language-select__flag-image--nl",
  },
  {
    value: "el",
    label: "Ελληνικά",
    flagClass: "language-select__flag-image--el",
  },
  {
    value: "sl",
    label: "Slovenščina",
    flagClass: "language-select__flag-image--sl",
  },
];

function LanguageSwitcher({ compact = false, variant = "default" }) {
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const isFlagVariant = variant === "flag";

  const flagComponents = isFlagVariant
    ? {
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
    }
    : undefined;

  const languageOptions = isFlagVariant
    ? LANGUAGES
    : compact
      ? LANGUAGES.map((lang) => ({ ...lang, label: lang.value.toUpperCase() }))
      : LANGUAGES;

  const formatLanguageOptionLabel = (option, { context }) => {
    if (!isFlagVariant) {
      return option.label;
    }

    if (context === "value") {
      return (
        <span className="language-select__flag-value" aria-label={option.label} title={option.label}>
          <span className={`language-select__flag-image ${option.flagClass}`} aria-hidden="true" />
        </span>
      );
    }

    return (
      <span className="language-select__flag-option" aria-label={option.label} title={option.label}>
        <span className={`language-select__flag-image language-select__flag-icon ${option.flagClass}`} aria-hidden="true" />
      </span>
    );
  };

  useEffect(() => {
    if (!i18n.language) {
      // detect browser language
      const browserLanguage = navigator?.language?.split("-")[0]; // z.B. 'de' aus 'de-DE'

      const defaultLanguage =
        LANGUAGES.find((lang) => lang?.value === browserLanguage) ||
        LANGUAGES[0]; // Fallback auf Englisch

      i18n.changeLanguage(defaultLanguage?.value);
      setSelectedLanguage(defaultLanguage);
    } else {
      const currentLanguage = LANGUAGES.find(
        (lang) => lang?.value === i18n.language,
      );

      setSelectedLanguage(currentLanguage);
    }
  }, [i18n]);

  const changeLanguage = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
    setSelectedLanguage(selectedOption);
  };

  return (
    <Select
      value={selectedLanguage && (isFlagVariant
        ? selectedLanguage
        : compact
          ? {
            ...selectedLanguage,
            label: selectedLanguage.value.toUpperCase(),
          }
          : selectedLanguage)}
      onChange={changeLanguage}
      options={languageOptions}
      formatOptionLabel={formatLanguageOptionLabel}
      components={flagComponents}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      placeholder={t("select_language")}
      className={`custom-select language-select ${isFlagVariant ? "language-select--flag" : ""}`}
      classNamePrefix="language-select"
      hideSelectedOptions={isFlagVariant}
      isSearchable={false}
      controlShouldRenderValue
      aria-label={t("select_language")}
    />
  );
}

export default LanguageSwitcher;
